import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'

interface SaveAsMasterDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string
    scenario: string
    onSave: (name: string, description: string) => Promise<void>
}

export function SaveAsMasterDialog({
    open,
    onOpenChange,
    objectID,
    scenario,
    onSave,
}: SaveAsMasterDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [isSaving, setIsSaving] = useState(false)

    const handleSave = async () => {
        if (!name.trim()) {
            toast({
                title: t('common.error'),
                description: t('objects.runbookNameRequired'),
                variant: 'destructive',
            })
            return
        }

        setIsSaving(true)
        try {
            await onSave(name.trim(), description.trim())
            
            toast({
                title: t('common.success'),
                description: t('objects.runbookSavedAsMaster'),
            })
            
            onOpenChange(false)
            setName('')
            setDescription('')
        } catch (error) {
            console.error('Error saving runbook as master:', error)
            toast({
                title: t('common.error'),
                description: handleApiError(error),
                variant: 'destructive',
            })
        } finally {
            setIsSaving(false)
        }
    }

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setName('')
            setDescription('')
        }
    }, [open])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>{t('objects.saveAsMaster')}</DialogTitle>
                    <DialogDescription>
                        {t('objects.saveAsMasterDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="runbook-name">
                            {t('objects.runbookName')} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="runbook-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('objects.enterRunbookName')}
                            disabled={isSaving}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="runbook-description">
                            {t('objects.runbookDescription')}
                        </Label>
                        <Textarea
                            id="runbook-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('objects.enterRunbookDescription')}
                            rows={4}
                            disabled={isSaving}
                        />
                    </div>

                    <div className="flex justify-end space-x-2 pt-4 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSaving}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={!name.trim() || isSaving}
                        >
                            {isSaving ? t('objects.saving') : t('common.save')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
